/* eslint-disable arrow-body-style */
/* eslint-disable no-unused-vars */
/**
 * class for payments / realy-money transactions
 */
export class OMT_Payments {
  /**
   * constructor
   */
  constructor() {
    this._catalog = [];
    this._defineSignals();
  }

  /**
   * define phaser signals
   */
  _defineSignals() {
    this.signals = {
      onIAPConsumed: new Phaser.Signal(),
    };
  }

  /**
   * get the catalog of purchasable items
   * @param {Function} callback
   * @returns {Array}
   */
  async getCatalogAsync() {
    // return cached catalog if available
    if (this._catalog.length > 0) return this._catalog;
    // fetch catalog from FB
    try {
      this._catalog = await FBInstant.payments.getCatalogAsync();
      if (!this._catalog) throw new Error('invalid catalog');
    } catch (error) {
      this._catalog = [];
    }
    return this._catalog;
  }

  /**
   * get the catalog of purchasable items, catalog must be loaded first by getCatalogAsync()
   * @returns {Array}
   */
  getCatalogSync() {
    return this._catalog;
  }

  /**
   * splits up Shop IDs into their rewards
   * @param {String} deal ID of shopItem
   */
  parseRewards(deal) {
    const retValue = {};
    const splitdeal = deal.productID.split('_');

    retValue.coins = parseFloat(splitdeal[2].slice(0, -1)) * 1000;
    retValue.booster = [];
    retValue.fullPrice = deal.fullPrice;

    for (let i = 3; i < splitdeal.length; i++) {
      if (splitdeal[i].startsWith('b')) {
        retValue.booster.push({
          item: splitdeal[i].replace('b', 'booster#'),
          amount: parseInt(splitdeal[i + 1], 10),
        });
      }
    }

    return retValue;
  }

  /**
   * converts the new object notation for Deals to the old one, that is still used
   * @param {Object} parsedDeal Object returned by parseRewards()
   */
  convertToOldProductData(parsedDeal) {
    const giftData = [];
    if (parsedDeal.coins >= 0) giftData.push(['coin', parsedDeal.coins]);
    for (let i = 0; i < parsedDeal.booster.length; i++) {
      giftData.push([parsedDeal.booster[i].item, parsedDeal.booster[i].amount]);
    }
    return {
      giftData,
      labels: {
        amount: parsedDeal.coins,
      },
    };
  }

  /**
   * buy and attempt to immediately consume a product
   * @param {Object} product product object
   * @param {Function} callback callback function
   * @param {Object} context context to apply callback to
   */
  buyAndConsumeProduct(product, callback) {
    // for debug / fake purchases
    if (!G.BuildEnvironment.production && !G.BuildEnvironment.testPurchaseSdk) {
      callback(product, null, true);
      const productData = this.convertToOldProductData(this.parseRewards(product));
      const purchaseMsg = new G.PurchaseMsg({
        success: true,
        reward: productData.giftData,
      });
      this.signals.onIAPConsumed.dispatch(product);

      OMT.transactionTracking.logRealMoneyTransaction(product.price, product.productID, product.priceCurrencyCode);
      return;
    }

    const adOverlay = new G.AdLoadOverlay();
    let purchaseAsyncTimedOut = true;

    // clear overlay after 30 seconds due to facebook bug of no error being thrown after long waits
    setTimeout(() => {
      if (purchaseAsyncTimedOut) {
        adOverlay.destroy();
        console.log('FBInstant.payments.purchaseAsync: resolve/reject timeout');
      }
    }, 30000);

    FBInstant.payments.purchaseAsync({
      productID: product.productID,
    }).then((purchase) => { // purchase success
      // Transaction trakcing
      OMT.transactionTracking.logRealMoneyTransaction(product.price, product.productID, product.priceCurrencyCode);

      // attempt to immediately consume the product
      this.consumeProduct(purchase, (success) => {
        adOverlay.destroy();
        callback(product, purchase, success);
        if (success) {
          const productData = this.convertToOldProductData(this.parseRewards(product));
          const purchaseMsg = new G.PurchaseMsg({
            success: true,
            reward: productData.giftData,
          });
          this.signals.onIAPConsumed.dispatch(product);
        }
      });

      purchaseAsyncTimedOut = false;
    }).catch((error) => { // purchase failed or cancelled
      adOverlay.destroy();
      console.log('FBInstant.payments.purchaseAsync ERROR', error);
      // eslint-disable-next-line no-new
      new G.PurchaseMsg({ success: false });
      callback(null, null, false);
      purchaseAsyncTimedOut = false;
    });
  }

  /**
   * consume a purchased product.
   * @param {Object} purchase purchase object returned from facebook
   * @param {Function} callback callback function
   */
  consumeProduct(purchase, callback) {
    FBInstant.payments.consumePurchaseAsync(purchase.purchaseToken).then(() => {
      callback(true);
    }).catch((error) => {
      console.log('ERROR: consumeProduct could not consume the product');
      callback(false);
    });
  }

  /**
   * check if the user has any unconsumed products from previous transactions.
   */
  async checkUnconsumedProducts() {
    FBInstant.payments.getPurchasesAsync().then((purchases) => {
      if (purchases.length > 0) {
        this.consumeDelayedProduct(purchases[0], () => {
          this.checkUnconsumedProducts(); // check for more purchases
        });
      }
    }).catch((error) => {
      console.log('ERROR: checkUnconsumedProducts() could not get purchases');
    });
  }

  /**
   * consume a delayed / queued product returned by facebook from a previous session.
   * @param {Object} purchase
   * @param {Function} callback
   */
  consumeDelayedProduct(purchase, callback) {
    this.consumeProduct(purchase, (success) => {
      if (!success) return;
      if (G.saveState) G.saveState.incrementIAPCount(); // keep track of IAP count
      const productData = this.convertToOldProductData(this.parseRewards(purchase));
      const reward = productData.giftData;
      if (!reward) { // no reward so execute callback to continue
        callback();
        return;
      }

      // get catalog before continuing
      this.getCatalog().then((catalog) => {
        const catalogItem = catalog.find((element) => { return purchase.productID === element.productID; });
        const transactionID = purchase.paymentID ? purchase.paymentID : '';

        OMT.transactionTracking.logRealMoneyTransaction(catalogItem.price, catalogItem.productID, catalogItem.priceCurrencyCode);

        // capture player stats
        if (DDNA.tracking.enabled) {
          const dataCapture = DDNA.tracking.getDataCapture();

          if (G.saveState.getLives() === 0) dataCapture.addToPlayerCharacterizationParam('zeroLivesRealMoneyPurchases', 1, false);
          dataCapture.addToPlayerCharacterizationParam('realMoneySpent', DDNA.utils.formatCurrencyValueAsInteger(catalogItem.price) / 100, false);
          dataCapture.addToPlayerCharacterizationSessionParam('realMoneySpentThisSession', DDNA.utils.formatCurrencyValueAsInteger(catalogItem.price) / 100);
          dataCapture.addToPlayerCharacterizationParam('coinValueBoughtWithRealMoney', G.gift.getGiftTotalCoinValue(productData.giftData), false);
          dataCapture.addToPlayerCharacterizationParam('realMoneyPurchasesMade', 1, true);

          // track coinshop pruchase
          DDNA.transactionHelper.trackCoinShopPurchase(productData.giftData, {
            transactionType: 'PURCHASE',
            tActionType: 'INVENTORY_PURCHASE',
            tGameArea: game.state.getCurrentState().key === 'Game' ? 'LEVEL' : 'MAP',
            transactionID,
          }, catalogItem);
        }

        // apply gift and show related message
        G.gift.applyGift(reward);
        const purchaseMsg = new G.PurchaseMsg({
          success: true,
          reward,
        });

        // purchase message completed
        purchaseMsg.onFinished.addOnce(() => {
          callback();
        });
      });
    });
  }
}
